import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Video, VideoOff, Mic, MicOff, Monitor, MonitorOff, MessageSquare,
  Send, Paperclip, Users, Lock, Phone, X, Image as ImageIcon, ChevronDown
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_BASE = process.env.REACT_APP_BACKEND_URL
  ? process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws")
  : `ws://${window.location.host}`;

const ICE_SERVERS = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
  { urls: "stun:stun2.l.google.com:19302" },
  { urls: "stun:stun3.l.google.com:19302" },
  { urls: "turn:openrelay.metered.ca:80", username: "openrelayproject", credential: "openrelayproject" },
  { urls: "turn:openrelay.metered.ca:443", username: "openrelayproject", credential: "openrelayproject" },
];

/* ── Join Screen ── */
const JoinScreen = ({ meetingInfo, onJoin, error }) => {
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [joining, setJoining] = useState(false);
  const [err, setErr] = useState(error || "");

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!displayName.trim() || !password.trim()) return;
    setErr("");
    setJoining(true);
    try {
      const res = await axios.post(`${API}/meetings/${meetingInfo.id}/join`, {
        password: password.trim(),
        display_name: displayName.trim(),
      });
      onJoin(res.data.peer_id, displayName.trim());
    } catch (err) {
      setErr(err.response?.data?.detail || "Failed to join");
    }
    setJoining(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4" data-testid="meeting-join-screen">
      <div className="max-w-md w-full bg-zinc-900 border border-white/10 p-8 space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-orange-500/10 flex items-center justify-center mx-auto mb-4">
            <Video className="w-8 h-8 text-orange-500" />
          </div>
          <h1 className="text-white font-bold text-xl uppercase tracking-tight">{meetingInfo.title}</h1>
          {meetingInfo.description && <p className="text-zinc-500 text-sm mt-1">{meetingInfo.description}</p>}
          <div className="flex items-center justify-center gap-3 mt-3 text-zinc-600 text-xs">
            <span className="flex items-center gap-1"><Users className="w-3 h-3" />{meetingInfo.participant_count}/{meetingInfo.max_participants}</span>
          </div>
        </div>
        <form onSubmit={handleJoin} className="space-y-4">
          <div>
            <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1">Your Name *</label>
            <input value={displayName} onChange={e => setDisplayName(e.target.value)} required placeholder="Enter your name" autoFocus className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2.5 text-sm focus:border-orange-500/50 outline-none" data-testid="meeting-join-name" />
          </div>
          <div>
            <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1"><Lock className="w-3 h-3 inline mr-1" />Password *</label>
            <input value={password} onChange={e => setPassword(e.target.value)} required placeholder="Meeting password" className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2.5 text-sm font-mono focus:border-orange-500/50 outline-none" data-testid="meeting-join-password" />
          </div>
          {err && <p className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 px-3 py-2" data-testid="meeting-join-error">{err}</p>}
          <button type="submit" disabled={joining} className="w-full py-3 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-black font-bold text-sm uppercase tracking-wider" data-testid="meeting-join-btn">
            {joining ? "Joining..." : "Join Meeting"}
          </button>
        </form>
        {!meetingInfo.is_active && <p className="text-red-400 text-xs text-center">This meeting has ended.</p>}
      </div>
    </div>
  );
};


/* ── Chat Sidebar ── */
const ChatSidebar = ({ messages, onSend, onSendFile, myPeerId }) => {
  const [text, setText] = useState("");
  const fileRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight);
  }, [messages]);

  const handleSend = (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    onSend(text.trim());
    setText("");
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert("Max 5MB"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const isImage = file.type.startsWith("image/");
      onSendFile(reader.result, file.name, file.type, isImage ? "image" : "file");
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  return (
    <div className="flex flex-col h-full bg-zinc-900 border-l border-white/10">
      <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
        <MessageSquare className="w-4 h-4 text-zinc-400" />
        <span className="text-white font-bold text-xs uppercase tracking-wider">Chat</span>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-2 space-y-2" data-testid="meeting-chat-messages">
        {messages.map(m => (
          <div key={m.id} className={`${m.sender_id === myPeerId ? "text-right" : ""}`}>
            <p className="text-zinc-500 text-[10px]">{m.sender_name}</p>
            {m.msg_type === "image" && m.file_data ? (
              <img src={m.file_data} alt={m.file_name} className="max-w-[200px] max-h-[150px] rounded mt-0.5 inline-block" />
            ) : m.msg_type === "file" && m.file_data ? (
              <a href={m.file_data} download={m.file_name} className="text-orange-400 text-xs underline mt-0.5 block">{m.file_name}</a>
            ) : (
              <p className={`text-sm inline-block px-2.5 py-1.5 rounded-lg max-w-[85%] ${m.sender_id === myPeerId ? "bg-orange-500/20 text-orange-200" : "bg-zinc-800 text-zinc-300"}`}>{m.text}</p>
            )}
          </div>
        ))}
      </div>
      <form onSubmit={handleSend} className="px-3 py-2 border-t border-white/10 flex gap-2">
        <button type="button" onClick={() => fileRef.current?.click()} className="p-2 text-zinc-500 hover:text-white flex-shrink-0" data-testid="meeting-chat-attach">
          <Paperclip className="w-4 h-4" />
        </button>
        <input ref={fileRef} type="file" accept="image/*,.pdf,.doc,.docx,.txt,.csv,.xlsx" onChange={handleFile} className="hidden" />
        <input value={text} onChange={e => setText(e.target.value)} placeholder="Type a message..." className="flex-1 bg-zinc-800 border border-white/10 text-white px-3 py-1.5 text-sm outline-none min-w-0" data-testid="meeting-chat-input" />
        <button type="submit" className="p-2 text-orange-500 hover:text-orange-400 flex-shrink-0" data-testid="meeting-chat-send">
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
};


/* ── Video Tile ── */
const VideoTile = ({ stream, displayName, muted, isLocal, isScreenShare }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && stream) {
      ref.current.srcObject = stream;
    }
  }, [stream]);

  return (
    <div className="relative bg-zinc-800 rounded overflow-hidden aspect-video flex items-center justify-center">
      {stream ? (
        <video ref={ref} autoPlay playsInline muted={isLocal || muted}
          className={`w-full h-full object-cover ${isScreenShare ? "object-contain bg-black" : ""}`} />
      ) : (
        <div className="w-16 h-16 rounded-full bg-zinc-700 flex items-center justify-center text-zinc-400 text-2xl font-bold uppercase">
          {(displayName || "?")[0]}
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-3 py-1.5">
        <span className="text-white text-xs font-medium">
          {displayName}{isLocal ? " (You)" : ""}{isScreenShare ? " — Screen" : ""}
        </span>
      </div>
    </div>
  );
};


/* ── Main Meeting Room ── */
const MeetingRoom = ({ meetingInfo, peerId, displayName }) => {
  const [localStream, setLocalStream] = useState(null);
  const [screenStream, setScreenStream] = useState(null);
  const [peers, setPeers] = useState({});
  const [chatMessages, setChatMessages] = useState([]);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [connected, setConnected] = useState(false);
  const [mediaReady, setMediaReady] = useState(false);

  const wsRef = useRef(null);
  const peerConnections = useRef({});
  const localStreamRef = useRef(null);

  // Setup local media FIRST — WebSocket waits for this
  useEffect(() => {
    const init = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30 } },
          audio: { echoCancellation: true, noiseSuppression: true }
        });
        setLocalStream(stream);
        localStreamRef.current = stream;
      } catch {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } },
            audio: true
          });
          setLocalStream(stream);
          localStreamRef.current = stream;
        } catch {
          try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: false, audio: true });
            setLocalStream(stream);
            localStreamRef.current = stream;
          } catch {
            setLocalStream(null);
          }
        }
      }
      setMediaReady(true);
    };
    init();
    return () => {
      localStreamRef.current?.getTracks().forEach(t => t.stop());
    };
  }, []);

  // WebSocket + WebRTC — only connect AFTER media is ready
  useEffect(() => {
    if (!peerId || !mediaReady) return;
    const wsUrl = `${WS_BASE}/api/meetings/${meetingInfo.id}/ws`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ peer_id: peerId, display_name: displayName }));
      setConnected(true);
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      handleSignal(data);
    };

    ws.onclose = () => setConnected(false);

    return () => {
      ws.close();
      Object.values(peerConnections.current).forEach(pc => pc.close());
      peerConnections.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peerId, meetingInfo.id, displayName, mediaReady]);

  const createPC = useCallback((remotePeerId, remoteName) => {
    if (peerConnections.current[remotePeerId]) return peerConnections.current[remotePeerId];

    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    peerConnections.current[remotePeerId] = pc;

    // Add local tracks
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        pc.addTrack(track, localStreamRef.current);
      });
    }

    // ICE candidates
    pc.onicecandidate = (e) => {
      if (e.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: "ice_candidate",
          target: remotePeerId,
          candidate: e.candidate,
        }));
      }
    };

    // Remote stream
    pc.ontrack = (e) => {
      setPeers(prev => ({
        ...prev,
        [remotePeerId]: {
          ...prev[remotePeerId],
          display_name: remoteName || prev[remotePeerId]?.display_name || "Guest",
          stream: e.streams[0],
        }
      }));
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === "failed") {
        // Attempt reconnection by restarting ICE
        pc.restartIce();
      } else if (pc.connectionState === "disconnected") {
        // Give it a moment — disconnected can recover on its own
        setTimeout(() => {
          if (pc.connectionState === "disconnected") {
            pc.close();
            delete peerConnections.current[remotePeerId];
          }
        }, 5000);
      }
    };

    return pc;
  }, []);

  const handleSignal = useCallback(async (data) => {
    switch (data.type) {
      case "room_state":
        setChatMessages(data.chat_history || []);
        // Create offers to all existing peers
        for (const p of (data.peers || [])) {
          setPeers(prev => ({ ...prev, [p.peer_id]: { display_name: p.display_name, stream: null } }));
          const pc = createPC(p.peer_id, p.display_name);
          try {
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            wsRef.current?.send(JSON.stringify({
              type: "offer", target: p.peer_id, sdp: pc.localDescription,
            }));
          } catch (e) { console.error("Offer error:", e); }
        }
        break;

      case "peer_joined": {
        setPeers(prev => ({ ...prev, [data.peer_id]: { display_name: data.display_name, stream: null } }));
        // Proactively create offer to the new peer (both sides offer for robustness)
        const pcNew = createPC(data.peer_id, data.display_name);
        try {
          const offer = await pcNew.createOffer();
          await pcNew.setLocalDescription(offer);
          wsRef.current?.send(JSON.stringify({
            type: "offer", target: data.peer_id, sdp: pcNew.localDescription,
          }));
        } catch (e) { console.error("Offer to new peer error:", e); }
        break;
      }

      case "offer": {
        let pc = peerConnections.current[data.from];
        if (pc) {
          // Handle glare: if we already have a connection, check signaling state
          if (pc.signalingState === "have-local-offer") {
            // Collision: both sides sent offers. Lower peer_id yields (polite peer)
            const isPolite = peerId > data.from;
            if (isPolite) {
              // Roll back our offer and accept theirs
              await pc.setLocalDescription({ type: "rollback" });
            } else {
              // Ignore their offer, they'll accept our answer
              break;
            }
          }
        } else {
          pc = createPC(data.from, data.display_name);
        }
        try {
          await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          wsRef.current?.send(JSON.stringify({
            type: "answer", target: data.from, sdp: pc.localDescription,
          }));
        } catch (e) { console.error("Answer error:", e); }
        break;
      }

      case "answer": {
        const pc = peerConnections.current[data.from];
        if (pc) {
          try { await pc.setRemoteDescription(new RTCSessionDescription(data.sdp)); }
          catch (e) { console.error("SetRemote error:", e); }
        }
        break;
      }

      case "ice_candidate": {
        const pc = peerConnections.current[data.from];
        if (pc && data.candidate) {
          try { await pc.addIceCandidate(new RTCIceCandidate(data.candidate)); }
          catch (e) { console.error("ICE error:", e); }
        }
        break;
      }

      case "peer_left":
        setPeers(prev => { const n = { ...prev }; delete n[data.peer_id]; return n; });
        if (peerConnections.current[data.peer_id]) {
          peerConnections.current[data.peer_id].close();
          delete peerConnections.current[data.peer_id];
        }
        break;

      case "chat":
        setChatMessages(prev => [...prev, data.message]);
        break;

      default:
        break;
    }
  }, [createPC, peerId]);

  // Controls
  const toggleMute = () => {
    localStream?.getAudioTracks().forEach(t => { t.enabled = !t.enabled; });
    setIsMuted(!isMuted);
  };

  const toggleVideo = () => {
    localStream?.getVideoTracks().forEach(t => { t.enabled = !t.enabled; });
    setIsVideoOff(!isVideoOff);
  };

  const toggleScreen = async () => {
    if (isScreenSharing) {
      screenStream?.getTracks().forEach(t => t.stop());
      setScreenStream(null);
      setIsScreenSharing(false);
      // Replace screen track with camera track in all PCs
      if (localStreamRef.current) {
        const videoTrack = localStreamRef.current.getVideoTracks()[0];
        if (videoTrack) {
          Object.values(peerConnections.current).forEach(pc => {
            const sender = pc.getSenders().find(s => s.track?.kind === "video");
            if (sender) sender.replaceTrack(videoTrack);
          });
        }
      }
      wsRef.current?.send(JSON.stringify({ type: "screen_share_stopped" }));
    } else {
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
        setScreenStream(stream);
        setIsScreenSharing(true);
        const screenTrack = stream.getVideoTracks()[0];
        // Replace camera track with screen track
        Object.values(peerConnections.current).forEach(pc => {
          const sender = pc.getSenders().find(s => s.track?.kind === "video");
          if (sender) sender.replaceTrack(screenTrack);
        });
        screenTrack.onended = () => toggleScreen();
        wsRef.current?.send(JSON.stringify({ type: "screen_share_started" }));
      } catch { /* User cancelled */ }
    }
  };

  const sendChat = (text) => {
    wsRef.current?.send(JSON.stringify({ type: "chat", text, msg_type: "text" }));
  };

  const sendFile = (fileData, fileName, fileType, msgType) => {
    wsRef.current?.send(JSON.stringify({
      type: "chat", text: "", msg_type: msgType,
      file_data: fileData, file_name: fileName, file_type: fileType,
    }));
  };

  const leaveMeeting = () => {
    localStream?.getTracks().forEach(t => t.stop());
    screenStream?.getTracks().forEach(t => t.stop());
    wsRef.current?.close();
    window.close();
    window.location.href = "/";
  };

  const peerList = Object.entries(peers);
  const totalTiles = 1 + peerList.length;
  const gridClass = totalTiles <= 1 ? "grid-cols-1" : totalTiles <= 2 ? "grid-cols-2" : totalTiles <= 4 ? "grid-cols-2" : "grid-cols-3";

  return (
    <div className="h-screen bg-[#0a0a0a] flex flex-col" data-testid="meeting-room">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-zinc-900 border-b border-white/10">
        <div className="flex items-center gap-3">
          <Video className="w-4 h-4 text-orange-500" />
          <span className="text-white font-bold text-sm">{meetingInfo.title}</span>
          <span className={`w-2 h-2 rounded-full ${connected ? "bg-green-500" : "bg-red-500"}`} />
        </div>
        <div className="flex items-center gap-2 text-zinc-500 text-xs">
          <Users className="w-3 h-3" />{1 + peerList.length} participant{peerList.length !== 0 ? "s" : ""}
        </div>
      </div>

      {/* Main area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Video grid */}
        <div className="flex-1 p-3 overflow-y-auto">
          <div className={`grid ${gridClass} gap-3 h-full auto-rows-fr`}>
            <VideoTile stream={isScreenSharing ? screenStream : localStream} displayName={displayName} isLocal muted isScreenShare={isScreenSharing} />
            {peerList.map(([pid, peer]) => (
              <VideoTile key={pid} stream={peer.stream} displayName={peer.display_name} />
            ))}
          </div>
        </div>

        {/* Chat sidebar */}
        {showChat && (
          <div className="w-80 flex-shrink-0 hidden md:flex flex-col" data-testid="meeting-chat-sidebar">
            <ChatSidebar messages={chatMessages} onSend={sendChat} onSendFile={sendFile} myPeerId={peerId} />
          </div>
        )}

        {/* Participants sidebar */}
        {showParticipants && (
          <div className="w-64 flex-shrink-0 bg-zinc-900 border-l border-white/10 overflow-y-auto" data-testid="meeting-participants-sidebar">
            <div className="px-4 py-3 border-b border-white/10">
              <span className="text-white font-bold text-xs uppercase tracking-wider">Participants ({1 + peerList.length})</span>
            </div>
            <div className="p-3 space-y-2">
              <div className="flex items-center gap-2 px-3 py-2 bg-zinc-800 rounded">
                <div className="w-7 h-7 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-500 text-xs font-bold uppercase">{displayName[0]}</div>
                <span className="text-white text-sm">{displayName} (You)</span>
              </div>
              {peerList.map(([pid, peer]) => (
                <div key={pid} className="flex items-center gap-2 px-3 py-2 bg-zinc-800 rounded">
                  <div className="w-7 h-7 rounded-full bg-zinc-700 flex items-center justify-center text-zinc-400 text-xs font-bold uppercase">{(peer.display_name || "?")[0]}</div>
                  <span className="text-white text-sm">{peer.display_name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Mobile chat drawer */}
      {showChat && (
        <div className="md:hidden fixed inset-0 z-50 bg-black/80 flex flex-col">
          <button onClick={() => setShowChat(false)} className="self-end p-4 text-white"><X className="w-5 h-5" /></button>
          <div className="flex-1 overflow-hidden">
            <ChatSidebar messages={chatMessages} onSend={sendChat} onSendFile={sendFile} myPeerId={peerId} />
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="flex items-center justify-center gap-3 px-4 py-3 bg-zinc-900 border-t border-white/10" data-testid="meeting-controls">
        <button onClick={toggleMute} className={`p-3 rounded-full transition-colors ${isMuted ? "bg-red-500 text-white" : "bg-zinc-800 text-white hover:bg-zinc-700"}`} data-testid="meeting-toggle-mic" title={isMuted ? "Unmute" : "Mute"}>
          {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
        </button>
        <button onClick={toggleVideo} className={`p-3 rounded-full transition-colors ${isVideoOff ? "bg-red-500 text-white" : "bg-zinc-800 text-white hover:bg-zinc-700"}`} data-testid="meeting-toggle-video" title={isVideoOff ? "Turn on camera" : "Turn off camera"}>
          {isVideoOff ? <VideoOff className="w-5 h-5" /> : <Video className="w-5 h-5" />}
        </button>
        <button onClick={toggleScreen} className={`p-3 rounded-full transition-colors ${isScreenSharing ? "bg-green-500 text-white" : "bg-zinc-800 text-white hover:bg-zinc-700"}`} data-testid="meeting-toggle-screen" title="Screen share">
          {isScreenSharing ? <MonitorOff className="w-5 h-5" /> : <Monitor className="w-5 h-5" />}
        </button>
        <button onClick={() => { setShowChat(!showChat); setShowParticipants(false); }} className={`p-3 rounded-full transition-colors ${showChat ? "bg-orange-500 text-black" : "bg-zinc-800 text-white hover:bg-zinc-700"}`} data-testid="meeting-toggle-chat" title="Chat">
          <MessageSquare className="w-5 h-5" />
        </button>
        <button onClick={() => { setShowParticipants(!showParticipants); setShowChat(false); }} className={`p-3 rounded-full transition-colors ${showParticipants ? "bg-orange-500 text-black" : "bg-zinc-800 text-white hover:bg-zinc-700"}`} data-testid="meeting-toggle-participants" title="Participants">
          <Users className="w-5 h-5" />
        </button>
        <button onClick={leaveMeeting} className="p-3 rounded-full bg-red-600 hover:bg-red-700 text-white ml-4" data-testid="meeting-leave" title="Leave meeting">
          <Phone className="w-5 h-5 rotate-[135deg]" />
        </button>
      </div>
    </div>
  );
};


/* ── Page Wrapper ── */
const MeetingPage = () => {
  const { meetingId } = useParams();
  const [meetingInfo, setMeetingInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [joined, setJoined] = useState(false);
  const [peerId, setPeerId] = useState(null);
  const [displayName, setDisplayName] = useState("");

  useEffect(() => {
    axios.get(`${API}/meetings/${meetingId}/info`)
      .then(r => setMeetingInfo(r.data))
      .catch(() => setError("Meeting not found"))
      .finally(() => setLoading(false));
  }, [meetingId]);

  const handleJoin = (pid, name) => {
    setPeerId(pid);
    setDisplayName(name);
    setJoined(true);
  };

  if (loading) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center"><div className="w-8 h-8 border-2 border-white/20 border-t-orange-500 rounded-full animate-spin" /></div>;
  if (error || !meetingInfo) return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
      <div className="text-center">
        <Video className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
        <h1 className="text-white text-xl font-bold mb-2">Meeting Not Found</h1>
        <p className="text-zinc-500 text-sm">{error || "This meeting doesn't exist or has been removed."}</p>
      </div>
    </div>
  );

  if (!joined) return <JoinScreen meetingInfo={meetingInfo} onJoin={handleJoin} error="" />;

  return <MeetingRoom meetingInfo={meetingInfo} peerId={peerId} displayName={displayName} />;
};

export default MeetingPage;
